// @ts-nocheck
// import LoadingSpinner from "@insite/mobius/LoadingSpinner"; // Starter Custom Code
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import DisablerContext from "@insite/mobius/utilities/DisablerContext";
import getProp from "@insite/mobius/utilities/getProp";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import safeColor from "@insite/mobius/utilities/safeColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
// import styled, { withTheme } from "styled-components"; // Starter Custom Code

/* Starter Custom Code - Start */
import BaseLoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import styled, { css, ThemeProps, withTheme } from "styled-components";

/* Store Custom Code - Start */
import getColor from "@insite/mobius/utilities/getColor";
import useHasHeader from "@store/Framework/Common/Hooks/useHasHeader";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import mergeBreakpointValues from "@toolkit/Framework/Common/Utilities/mergeBreakpointValues";
import getValuesFromBreakpoint from "@toolkit/Framework/Common/Utilities/getValuesFromBreakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import getFullBreakpointValues from "@toolkit/Framework/Common/Utilities/getFullBreakpointValues";
import { StyledTheme } from "@toolkit/Framework/Types/StyledTheme";

const getViewportTopOffsets = (theme: StyledTheme, centerOffset?: number, hasHeader = true) => {
    const breakpointCount = theme.breakpoints.keys.length;

    // No site header on the current page: center within the full viewport without the header offset.
    if (!hasHeader) {
        return [`calc(50vh${centerOffset ? ` - ${centerOffset}px` : ""})`];
    }

    return mergeBreakpointValues(
        getFullBreakpointValues(theme.design.header.height, breakpointCount),
        getValuesFromBreakpoint(
            getFullBreakpointValues(theme.design.menu.tertiaryNavigation.height, breakpointCount),
            "md",
            theme,
        ),
        (a, b) => `(${a} + ${b})`,
        true,
        breakpointCount,
    ).map((x: any) => (x ? `calc(${x} + ((100vh - ${x}) / 2)${centerOffset ? ` - ${centerOffset}px` : ""})` : null));
};
/* Store Custom Code - End */

type LoadingOverlayStyleProps = Pick<LoadingOverlayProps, "css" | "minHeight"> & {
    isLoading?: LoadingOverlayProps["loading"];
    size?: LoadingSpinnerProps["size"];
    // Store Custom Code
    measuredHeight?: number;
    hasHeader?: boolean;
};

const LoadingSpinner = styled(BaseLoadingSpinner)`
    position: relative;
    ${({ size }) => `
        top: -${(size || 40) / 2}px;
        left: -${(size || 40) / 2}px;
    `}
`;

const Wrapper = styled.div`
    max-height: 99.9%; /* Fix an overflow issue */
`;
/* Starter Custom Code - End */

interface LoadingOverlayPresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<LoadingOverlayProps>;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
}

export type LoadingOverlayProps = MobiusStyledComponentProps<
    "div",
    LoadingOverlayPresentationProps & {
        /** CSS string or styled-components function to be injected into this component. */
        /** Show/hide status of the backdrop and spinner. */
        loading?: boolean;
        /** Props to be passed to the inner LoadingSpinner component. */
        spinnerProps?: LoadingSpinnerProps; // Starter Custom Code
        // Starter Custom Code
        /** min-height of the container when the spinner is visible */
        minHeight?: number | null;
        // Store Custom Code
        isPlacingOrder?: boolean;
        displayLoadingText?: boolean;
        loadingText?: string;
        displayAdditionalMessage?: boolean;
        additionalMessageText?: string | React.ReactElement;
    }
>;

// Starter Custom - next line - LoadingOverlayStyleProps
const LoadingOverlayStyle = styled.div<LoadingOverlayStyleProps>`
    display: block; /* Starter Custom Code */
    position: relative;
    width: 100%;
    & > div:first-child {
        height: 100%;
        width: 100%;
        background: ${props =>
            safeColor(props.theme.colors.common.background).rgb().alpha(0.8).string()}; /* Store Custom Code */
        will-change: opacity;
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
        ${injectCss}
        &[data-loading="true"] {
            opacity: 1;
            position: absolute;
            z-index: ${getProp("theme.zIndex.loadingOverlay")};
            /* Starter Custom Code - Start */
            top: 0;
            left: 0;
            /* Starter Custom Code - End */
            /* Store Custom Code - Start */
            ~ * .loading-overlay-wrapper {
                display: none;
            }
            /* Store Custom Code - End */
        }
        &[data-loading="false"] {
            position: fixed;
            display: none;
        }
    }
    /* Starter Custom Code - Start */
    ${({ size, minHeight, isLoading, theme, measuredHeight /* Store Custom Code */ }) => {
        if (isLoading) {
            /* Store Custom Code - Start */
            const baseMinHeight = minHeight || size || theme.loadingSpinner.defaultProps?.size || 40;
            const resolvedMinHeight = measuredHeight && measuredHeight > baseMinHeight ? measuredHeight : baseMinHeight;
            return css`
                min-height: ${resolvedMinHeight}px;
            `;
            /* Store Custom Code - End */
        }
    }}
    /* Starter Custom Code - End */
    ${injectCss}
`;

/* Store Custom Code - Start */
const SpinnerWrapper = styled.div<LoadingOverlayStyleProps>`
    /* Store Custom Code - Start */
    ${({ theme, size, minHeight, hasHeader }) => {
        const _size = size || 40;
        let offset = ((minHeight || 0) - _size) / 2;
        offset = offset > 0 ? offset : 0;

        return css`
            position: sticky;
            width: 1px;
            height: ${_size}px;
            margin: ${_size / 2 + offset}px auto ${-(_size / 2) + offset}px;
            ${breakpointValues(getViewportTopOffsets(theme, undefined, hasHeader), "top")}
        `;
    }}
`;

const ModalStyledSpinnerWrapper = styled.div<LoadingOverlayStyleProps>`
    position: sticky;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${pxToRem(24)};
    margin: auto;
    padding: ${pxToRem(50)};
    width: ${pxToRem(500)};
    max-width: 80%;
    border-radius: ${pxToRem(10)};
    background-color: ${getColor("common.background")};
    box-shadow: ${x => x.theme.shadows["3"]};

    ${({ theme, minHeight, measuredHeight, hasHeader }) => {
        const _measuredHeight = measuredHeight || 200;
        let offset = ((minHeight || 0) - _measuredHeight) / 2;
        offset = offset > 0 ? offset : 0;

        return css`
            margin-top: ${offset}px;
            margin-bottom: ${offset}px;
            ${breakpointValues(getViewportTopOffsets(theme, _measuredHeight / 2, hasHeader), "top")}
        `;
    }}
`;

const NestedSpinnerWrapper = styled.div`
    margin: auto;

    > svg {
        top: 0;
        left: 0;
    }
`;

const MessageTextWrapper = styled.span`
    font-weight: ${x => x.theme.design.typography.weight.bold};
    text-align: center;
`;
/* Store Custom Code - End */

/**
 * Adds a layer over the content to indicate that it is loading.
 */
const LoadingOverlay: React.FC<LoadingOverlayProps> = withTheme(
    ({
        children,
        mergeCss,
        loading = true,
        spinnerProps,
        theme,
        /* Store Custom Code - Start */
        isPlacingOrder,
        displayLoadingText,
        loadingText,
        displayAdditionalMessage,
        additionalMessageText,
        /* Store Custom Code - End */
        ...otherProps
    }) => {
        const { applyStyledProp } = applyPropBuilder(
            { children, mergeCss, loading, spinnerProps, theme, ...otherProps },
            { component: "loadingOverlay" },
        );
        const resolvedMergeCss = mergeCss ?? otherProps?.theme?.loadingOverlay?.defaultProps?.mergeCss;

        /* Store Custom Code - Start */
        const hasHeader = useHasHeader();
        const [infoboxDisplay, setInfoBoxDisplay] = React.useState<React.ReactElement>(<></>);

        React.useEffect(() => {
            if (displayAdditionalMessage && additionalMessageText) {
                setTimeout(() => {
                    setInfoBoxDisplay(<MessageTextWrapper>{additionalMessageText}</MessageTextWrapper>);
                }, 2000);
            }
        }, []);

        // Measure the modal spinner wrapper's real rendered height so it can be centered accurately,
        // reacting to content changes (delayed additional message, font/text reflow, responsive size).
        const modalSpinnerRef = React.useRef<HTMLDivElement>(null);
        const [modalSpinnerHeight, setModalSpinnerHeight] = React.useState<number>();

        React.useEffect(() => {
            const element = modalSpinnerRef.current;
            if (!element || typeof ResizeObserver === "undefined") {
                return;
            }
            const observer = new ResizeObserver(([entry]) => {
                const borderBoxHeight = entry.borderBoxSize?.[0]?.blockSize ?? element.getBoundingClientRect().height;
                setModalSpinnerHeight(borderBoxHeight);
            });
            observer.observe(element);
            return () => observer.disconnect();
        }, [displayLoadingText, displayAdditionalMessage, isPlacingOrder]);
        /* Store Custom Code - End */

        return (
            <LoadingOverlayStyle
                css={applyStyledProp("css", resolvedMergeCss)}
                {...otherProps}
                // Starter Custom Code
                isLoading={loading}
                size={spinnerProps?.size}
                // Store Custom Code
                measuredHeight={modalSpinnerHeight}
            >
                {/* Starter Custom Code - next-line - Wrapper */}
                <Wrapper
                    data-loading={loading}
                    data-test-selector="loadingOverlaySpinner"
                    role="alert"
                    aria-live="assertive"
                    // Store Custom Code
                    className="loading-overlay-wrapper"
                >
                    <VisuallyHidden>
                        {loading ? theme.translate("loading content") : theme.translate("content loaded")}
                    </VisuallyHidden>
                    {/* Store Custom Code - Start */}
                    {displayLoadingText || (isPlacingOrder && displayAdditionalMessage) ? (
                        <ModalStyledSpinnerWrapper
                            minHeight={otherProps.minHeight}
                            size={spinnerProps?.size}
                            ref={modalSpinnerRef}
                            measuredHeight={modalSpinnerHeight}
                            hasHeader={hasHeader}
                        >
                            <NestedSpinnerWrapper>
                                <LoadingSpinner {...spinnerProps} />
                            </NestedSpinnerWrapper>
                            {displayLoadingText && <MessageTextWrapper>{loadingText}</MessageTextWrapper>}
                            {isPlacingOrder && displayAdditionalMessage && infoboxDisplay}
                        </ModalStyledSpinnerWrapper>
                    ) : (
                        <SpinnerWrapper
                            minHeight={otherProps.minHeight}
                            size={spinnerProps?.size}
                            hasHeader={hasHeader}
                        >
                            <LoadingSpinner {...spinnerProps} />
                        </SpinnerWrapper>
                    )}
                    {/* Store Custom Code - End */}
                </Wrapper>
                <DisablerContext.Provider value={{ disable: loading }}>{children}</DisablerContext.Provider>
            </LoadingOverlayStyle>
        );
    },
);

/** @component */
export default LoadingOverlay;

export { LoadingOverlayStyle };
